import { useEffect, useState } from 'react'
import ResearchShell from '../components/research/ResearchShell'
import AnalysisResultView from '../components/research/AnalysisResultView'
import { SECTORS, type Filing, type FormType, type OpusAnalysisResponse } from '../types/research'
import { analyzeWithOpus, isResearchUsingMockData, searchFilings } from '../lib/research-api'

const FORM_TYPES: FormType[] = ['10-K', '10-Q', '8-K', 'S-1', 'S-3', '424B5', 'DEF 14A']

function FilingRow({ filing }: { filing: Filing }) {
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [running, setRunning] = useState(false)
  const [analysis, setAnalysis] = useState<OpusAnalysisResponse | null>(null)

  async function runAnalysis() {
    setRunning(true)
    try {
      const result = await analyzeWithOpus({
        task: 'analyze-filing',
        symbol: filing.symbol,
        accessionNumber: filing.accessionNumber,
        prompt: question.trim() || undefined,
      })
      setAnalysis(result)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="py-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400">
              {filing.formType}
            </span>
            <span className="font-semibold text-sm text-slate-900 dark:text-white">{filing.symbol}</span>
            <span className="text-sm text-slate-500 dark:text-slate-400 truncate">{filing.company}</span>
            {filing.sector && <span className="text-xs text-slate-500 hidden sm:inline">{filing.sector}</span>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Filed {filing.filedAt}
            {filing.periodEnd && ` · period ended ${filing.periodEnd}`} · {filing.description}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={filing.edgarUrl}
            target="_blank"
            rel="noreferrer"
            className="px-2.5 py-1.5 text-xs bg-slate-200/70 dark:bg-slate-700/50 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg transition-colors"
          >
            EDGAR ↗
          </a>
          <button
            onClick={() => setOpen(!open)}
            className={`px-2.5 py-1.5 text-xs rounded-lg transition-colors ${
              open
                ? 'bg-indigo-600 text-white'
                : 'bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'
            }`}
          >
            🧠 Opus 5 analysis
          </button>
        </div>
      </div>

      {/* Inline Opus panel */}
      {open && (
        <div className="mt-3 bg-slate-100/70 dark:bg-slate-900/50 border border-indigo-500/20 rounded-lg p-4">
          <div className="flex gap-2 mb-3">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !running && runAnalysis()}
              placeholder={`Ask Opus about this ${filing.formType} — leave blank for a full breakdown`}
              className="flex-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-indigo-500 outline-none placeholder-slate-400 dark:placeholder-slate-500"
            />
            <button
              onClick={runAnalysis}
              disabled={running}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            >
              {running ? 'Analyzing…' : 'Analyze'}
            </button>
          </div>
          {running && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-3">
              <div className="w-4 h-4 border-2 border-slate-300 dark:border-slate-600 border-t-indigo-500 rounded-full animate-spin" />
              Opus is reading the filing…
            </div>
          )}
          {!running && analysis && <AnalysisResultView result={analysis} />}
        </div>
      )}
    </div>
  )
}

export default function FilingsPage() {
  const [mode, setMode] = useState<'symbol' | 'sector'>('symbol')
  const [symbol, setSymbol] = useState('')
  const [sector, setSector] = useState<string>(SECTORS[0])
  const [forms, setForms] = useState<FormType[]>([])
  const [filings, setFilings] = useState<Filing[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [isMock, setIsMock] = useState(false)

  async function run() {
    if (mode === 'symbol' && !symbol.trim()) return
    setLoading(true)
    setSearched(true)
    try {
      const results = await searchFilings({
        symbol: mode === 'symbol' ? symbol.trim().toUpperCase() : undefined,
        sector: mode === 'sector' ? sector : undefined,
        formTypes: forms.length ? forms : undefined,
      })
      setFilings(results)
      setIsMock(isResearchUsingMockData())
    } finally {
      setLoading(false)
    }
  }

  // Sector mode: auto-search on load and when scope changes
  useEffect(() => {
    if (mode === 'sector') run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sector, forms])

  function toggleForm(f: FormType) {
    setForms((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]))
  }

  return (
    <ResearchShell showMockBanner={isMock && !loading}>
      {/* Search controls */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex gap-1 mb-3">
          {(['symbol', 'sector'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                mode === m
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              By {m}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mb-3">
          {mode === 'symbol' ? (
            <>
              <input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run()}
                placeholder="Ticker symbol (e.g. AAPL)"
                className="flex-1 bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none placeholder-slate-400 dark:placeholder-slate-500"
              />
              <button
                onClick={run}
                disabled={loading || !symbol.trim()}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {loading ? 'Searching…' : 'Search'}
              </button>
            </>
          ) : (
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="flex-1 bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            >
              {SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Form-type filters */}
        <div className="flex flex-wrap gap-1.5">
          {FORM_TYPES.map((f) => (
            <button
              key={f}
              onClick={() => toggleForm(f)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                forms.includes(f)
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-200/70 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {f}
            </button>
          ))}
          {forms.length > 0 && (
            <button onClick={() => setForms([])} className="px-2.5 py-1 text-xs text-slate-500 hover:underline">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {loading && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          ))}
        </div>
      )}

      {!loading && searched && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{filings.length} filings</p>
          <div className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
            {filings.map((f) => (
              <FilingRow key={f.accessionNumber} filing={f} />
            ))}
          </div>
          {filings.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-8">No filings found for this search.</p>
          )}
        </div>
      )}

      {!loading && !searched && mode === 'symbol' && (
        <div className="text-center py-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <div className="text-4xl mb-3">📄</div>
          <p className="text-slate-900 dark:text-white font-medium">Search SEC filings</p>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 max-w-md mx-auto">
            Look up filings by ticker or browse a whole sector, then open the Opus 5 panel on any
            filing to analyze it — offerings, risk factors, conversion clauses, and more.
          </p>
        </div>
      )}
    </ResearchShell>
  )
}
